// web/src/components/AccountPage.tsx
import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Account } from "./TopMenuBar";
import PageLayout from "./PageLayout";
import Avatar from "./Avatar";
import LimitReachedModal from "./LimitReachedModal";
import { useViewport } from "../hooks/useViewport";
import { ANDROID_TABLET_MENU_OFFSET, ANDROID_TABLET_SCROLL_REDUCTION } from "../lib/androidLayout";
import { increment as incrementPlayGamesAchievement } from "../lib/playGamesAchievements";
import { useAchievements } from "./AchievementsProvider";
import type { GooglePlayBillingPlugin } from "../types/google-play-billing";
import { isAdsRemoved, setAdsRemoved } from "../lib/unityAds";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const LEADERBOARD_BG = "LeaderboardBackground.png";

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

const REMOVE_ADS_PRODUCT_ID = "remove_ads";

const getGooglePlayBilling = (): GooglePlayBillingPlugin | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    return null;
  }
  if (cap.Plugins?.GooglePlayBilling) {
    return cap.Plugins.GooglePlayBilling as GooglePlayBillingPlugin;
  }
  if (cap.getPlugin) {
    try {
      return cap.getPlugin("GooglePlayBilling") as GooglePlayBillingPlugin;
    } catch {
      return null;
    }
  }
  return null;
};

// Product name mapping (matches server/store_products.py)
const PRODUCT_NAMES: { [productId: string]: string } = {
  "tin_pan": "Tin Pan Starter",
  "steel_pan": "Steel Pan Kit",
  "assayers_pack": "Assayer's Pack",
  "claim_jumper_rig": "Claim Jumper Rig",
  "expedition_crate": "Expedition Crate",
  "motherlode_vault": "Motherlode Vault",
};

// Verify Email Button Component
function VerifyEmailButton({ account }: { account: Account }) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendVerification = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/email/verify/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      if (response.ok) {
        setSent(true);
        setTimeout(() => setSent(false), 3000);
      }
    } catch (err) {
      console.error("Failed to send verification email:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={handleSendVerification}
      disabled={sending || sent}
      className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all disabled:opacity-50`}
      style={{
        background: '#fffaeb',
        color: '#3e2615',
        boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
        border: '2px solid #8b6542'
      }}
    >
      {sending ? "Sending..." : sent ? "Sent! Check your email" : "Resend Verification Email"}
    </button>
  );
}

interface AccountPageProps {
  account: Account;
  onAccountRefresh?: () => void;
  onLogout?: () => void;
}

export default function AccountPage({ account, onAccountRefresh, onLogout }: AccountPageProps) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const { refresh: refreshAchievements } = useAchievements();
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  
  // Bulletproof iPhone detection - exclude iPad (must be after viewport declaration)
  const isIPhone = (() => {
    if (!isIOSApp) return false;
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return false;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform.toLowerCase() === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    
    // If dimensions suggest iPad, exclude it
    if (maxDim >= 768 && minDim >= 768) return false;
    
    // Explicit iPhone in user agent (but not iPad)
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (userAgent.includes('ipod')) return true;
    
    // If it's iOS app and not iPad (by dimensions), assume iPhone
    if (isIOSApp && (maxDim < 768 || minDim < 768)) return true;
    
    return false;
  })();
  
  // Bulletproof iPad detection - exclude iPhone (must be after isIPhone declaration)
  const isIOSPad = (() => {
    if (!isIOSApp) return false;
    if (isIPhone) return false; // If it's iPhone, it's not iPad
    return Math.max(viewport.width, viewport.height) >= 768;
  })();
  const isAndroidTablet = viewport.isAndroidTablet;
  const isIOSLarge = isIOSApp && !isIPhone && viewport.width >= 768; // Exclude iPhone
  const androidInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) : 0;
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const scrollReduction = isAndroidTablet ? ANDROID_TABLET_SCROLL_REDUCTION : 0;
  const textScale = isAndroidTablet ? 3.6 : (isIOSPad ? 4 : (isIOSApp ? 1.14 : 1)); // bump overall tablet text
  const androidContentShift = isAndroidTablet ? 25 : 0;
  const androidBodyFontSize = isAndroidTablet ? '18px' : undefined;
  const androidBodyLineHeight = isAndroidTablet ? 1.3 : undefined;
  const iosPadGutterX = isIOSPad ? Math.round(viewport.width * 0.05) : 0;
  const iosPadGutterY = isIOSPad ? Math.round(viewport.height * 0.05) : 0;
  const iosPadShiftX = isIOSPad ? 10 : 0;
  const iosPadShiftY = isIOSPad ? 24 : 0;
  const androidTextStyle = isAndroidTablet ? (
    <style>{`
      .android-tablet-text-account, .android-tablet-text-account * {
        font-size: ${androidBodyFontSize};
        line-height: ${androidBodyLineHeight};
      }
    `}</style>
  ) : null;
  const iosTopPad = isIOSApp ? (isIOSLarge ? 76 : 40) : 0;
  const iosBottomPad = isIOSApp ? (isIOSLarge ? 120 : 70) : 0;
  const backgroundSrc = (isIOSPad || isIPhone) ? "TabletBackground.png" : LEADERBOARD_BG;
  // iPhone-specific adjustments: move right and make narrower (matching Leaderboard)
  const iphoneRightShift = isIPhone ? 10 : 0; // Move right by 10px (keeps left side in place)
  const iphoneRightNarrow = isIPhone ? 27 : 0; // Add 27px to right to make narrower
  const scrollRef = React.useRef<HTMLDivElement>(null);
  
  // Font sizes for iOS iPad
  const iosPadHeaderFontSize = isIOSPad && isMobileLandscape ? '20px' : undefined;
  const iosPadBodyFontSize = isIOSPad && isMobileLandscape ? '16px' : undefined;
  const iosPadSmallFontSize = isIOSPad && isMobileLandscape ? '14px' : undefined;
  const iosPadAvatarSize = isIOSPad && isMobileLandscape ? 80 : (isMobileLandscape ? 48 : 64);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const [resetPasswordStatus, setResetPasswordStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [resetPasswordMessage, setResetPasswordMessage] = useState("");
  const [showCompleteRegistrationModal, setShowCompleteRegistrationModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositStatus, setDepositStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [depositMessage, setDepositMessage] = useState("");
  const [adsRemovedLocal, setAdsRemovedLocal] = useState(isAdsRemoved());
  const [removeAdsStatus, setRemoveAdsStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [removeAdsMessage, setRemoveAdsMessage] = useState("");
  const bankProgressCarryRef = useRef(0);

  const hasRealEmail = Boolean(account.email && !account.email.endsWith("@guest.local"));
  const isGuestAccount = !hasRealEmail;

  useEffect(() => {
    const handleAdsRemoved = () => {
      setAdsRemovedLocal(isAdsRemoved());
    };
    window.addEventListener("adsRemovedChanged", handleAdsRemoved as EventListener);
    return () => window.removeEventListener("adsRemovedChanged", handleAdsRemoved as EventListener);
  }, []);

  const updateScrollIndicators = (el: HTMLDivElement | null) => {
    if (!el) return;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };

  const handleResetPassword = async () => {
    if (!account.email) {
      setResetPasswordMessage("Email not available");
      setResetPasswordStatus("error");
      return;
    }

    setResetPasswordStatus("loading");
    setResetPasswordMessage("");

    try {
      const response = await fetch(`${API_BASE}/password/forgot`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: account.email }),
      });

      if (response.ok) {
        setResetPasswordStatus("success");
        setResetPasswordMessage("Password reset email sent! Check your inbox.");
      } else {
        setResetPasswordStatus("error");
        setResetPasswordMessage("Failed to send reset email. Please try again.");
      }
    } catch (err) {
      setResetPasswordStatus("error");
      setResetPasswordMessage("Network error. Please try again.");
    }
  };

  const handleDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDepositStatus("loading");
    setDepositMessage("");

    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      setDepositStatus("error");
      setDepositMessage("Please enter a valid amount greater than 0");
      return;
    }

    const activeBalance = parseFloat(account.glint_balance);
    if (amount > activeBalance) {
      setDepositStatus("error");
      setDepositMessage("Insufficient active glint balance");
      return;
    }

    try {
      // Check for regular user JWT token first (for logged-in users on native apps)
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        token = localStorage.getItem('regular_user_jwt');
      }
      
      // Fall back to guest token if no regular user token
      if (!token) {
        const { getValidGuestToken } = await import('../lib/guest-auth');
        token = await getValidGuestToken();
      }
      
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}/banking/deposit`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ amount }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        setDepositStatus("error");
        setDepositMessage(errorText || "Failed to deposit glint");
        return;
      }

      const result = await response.json();
      setDepositStatus("success");
      setDepositMessage(`Successfully deposited ✧${amount.toFixed(2)} to bank`);
      setDepositAmount("");

      // Carry fractional glint so incremental steps stay whole numbers.
      bankProgressCarryRef.current += amount;
      const bankSteps = Math.floor(bankProgressCarryRef.current);
      if (bankSteps > 0) {
        bankProgressCarryRef.current -= bankSteps;
        void incrementPlayGamesAchievement("nest_egg", bankSteps);
        void incrementPlayGamesAchievement("growing_reserves", bankSteps);
        void incrementPlayGamesAchievement("vault_keeper", bankSteps);
      }
      
      // Refresh account to show updated balances
      if (onAccountRefresh) {
        await onAccountRefresh();
      }

      await refreshAchievements();
      
      // Fetch updated account and dispatch update event
      try {
        const { getValidGuestToken: getToken } = await import('../lib/guest-auth');
        const token = await getToken();
        const accountHeaders: HeadersInit = {};
        if (token) {
          accountHeaders["Authorization"] = `Bearer ${token}`;
        }
          const accountResponse = await fetch(`${API_BASE}/account`, {
          credentials: "include",
          headers: accountHeaders
        });
        if (accountResponse.ok) {
          const accountData = await accountResponse.json();
          window.dispatchEvent(new CustomEvent('accountUpdated', { detail: accountData }));
        }
      } catch (err) {
        // Non-critical - account refresh will handle it
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setDepositStatus("idle");
        setDepositMessage("");
      }, 3000);
    } catch (err) {
      setDepositStatus("error");
      setDepositMessage("Network error. Please try again.");
    }
  };

  const handleRemoveAdsPurchase = async () => {
    if (adsRemovedLocal || removeAdsStatus === "loading") {
      return;
    }
    setRemoveAdsStatus("loading");
    setRemoveAdsMessage("");

    const billing = getGooglePlayBilling();
    if (!billing) {
      setRemoveAdsStatus("error");
      setRemoveAdsMessage("Google Play Billing is not available.");
      return;
    }

    try {
      const purchase = await billing.purchaseProduct({ productId: REMOVE_ADS_PRODUCT_ID });
      if (!purchase || !purchase.purchaseToken) {
        throw new Error("Purchase was cancelled or failed.");
      }
      setAdsRemoved(true);
      setAdsRemovedLocal(true);
      setRemoveAdsStatus("success");
      setRemoveAdsMessage("Ads removed on this device.");
    } catch (err: any) {
      const message = err?.message || String(err);
      if (message.toLowerCase().includes("cancel")) {
        setRemoveAdsStatus("idle");
        setRemoveAdsMessage("");
        return;
      }
      setRemoveAdsStatus("error");
      setRemoveAdsMessage(message);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => updateScrollIndicators(scrollRef.current));
    }
  }, [isMobileLandscape]);

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {(isAndroid || isIOSApp || isIPhone) && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(backgroundSrc)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center" }}
            draggable={false}
          />
        </div>
      )}
      {androidTextStyle}
      <div 
        className={`${isMobileLandscape ? "fixed z-40 p-2 flex flex-col" : "px-2 md:px-6 pb-4"} ${isAndroidTablet ? "android-tablet-text-account" : ""}`}
        style={isMobileLandscape ? {
          top: isAndroidTablet && isMobileLandscape
            ? `calc(32px + ${androidMenuOffset}px)`
            : (isAndroid && isMobileLandscape ? '32px' : (isIPhone && isMobileLandscape ? '32px' : `calc(env(safe-area-inset-top, 0px) + ${iosTopPad}px + ${isIOSPad ? iosPadGutterY / 2 + iosPadShiftY : 0}px)`)),
          left: isAndroid
            ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
            : isIPhone
            ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iphoneRightShift}px)`
            : `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${isIOSPad ? iosPadGutterX : 0}px)`,
          right: isAndroid
            ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
            : isIPhone
            ? `calc(env(safe-area-inset-right, 0px) + 48px + ${iphoneRightNarrow}px)`
            : `calc(env(safe-area-inset-right, 0px) + 48px + ${isIOSPad ? iosPadGutterX : 0}px)`,
          bottom: scrollReduction
            ? (isAndroid ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${scrollReduction}px)` : (isIPhone ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 56px + ${scrollReduction}px)` : `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${scrollReduction}px + ${iosBottomPad}px)`))
            : (isAndroid ? 'calc(env(safe-area-inset-bottom, 8px) - 14px + 50px)' : (isIPhone ? 'calc(env(safe-area-inset-bottom, 8px) - 14px + 56px)' : `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${iosBottomPad}px + ${isIOSPad ? iosPadGutterY / 2 : 0}px)`)), // iPhone matches Leaderboard (56px), others use 50px
          overflow: 'visible',
          marginLeft: '-20px',
          marginRight: 0,
          fontSize: textScale > 1 ? `${textScale}em` : undefined,
          lineHeight: textScale > 1 ? 1.2 : undefined,
          transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
          ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
        } : (isAndroid ? { paddingLeft: `${androidInsetLeft}px`, paddingRight: `${androidInsetRight}px`, fontSize: textScale > 1 ? `${textScale}em` : undefined, lineHeight: textScale > 1 ? 1.2 : undefined } : (textScale > 1 ? { fontSize: `${textScale}em`, lineHeight: 1.2 } : {}))}
      >
        {!isMobileLandscape && (
          <h1 className="text-3xl font-bold mb-6" style={{ color: '#3e2615' }}>Account</h1>
        )}

        <div className={isMobileLandscape ? "relative flex-1 min-h-0" : ""} style={isMobileLandscape ? { overflow: 'visible' } : {}}>
          <div
            ref={scrollRef}
            onScroll={(e) => updateScrollIndicators(e.currentTarget)}
            className={isMobileLandscape ? "h-full overflow-y-auto overflow-x-auto pr-1" : ""}
            style={isMobileLandscape ? { paddingRight: '4px' } : {}}
          >
        {/* Two-column layout - responsive */}
        <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-6'}`}>
          {/* Left Column - Profile */}
          <div>
            <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-3' : 'p-6'}`} style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542'
            }}>
              <h2 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold ${isMobileLandscape ? 'mb-2' : 'mb-4'}`} style={{ 
                color: '#3e2615',
                fontSize: iosPadHeaderFontSize
              }}>Profile</h2>
              <div className={`flex items-center ${isMobileLandscape ? 'gap-2 mb-2' : 'gap-4 mb-4'}`}>
                <Avatar name={account.username} size={iosPadAvatarSize} />
                <div>
                  <div className={`${isMobileLandscape ? 'text-sm' : 'text-lg'} font-semibold`} style={{ 
                    color: '#3e2615',
                    fontSize: isIOSPad && isMobileLandscape ? '18px' : undefined
                  }}>
                    {account.username}
                  </div>
                  {account.email && (
                    <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm'}`} style={{ 
                      color: '#3e2615', 
                      opacity: 0.8,
                      fontSize: iosPadSmallFontSize
                    }}>
                      {account.email}
                    </div>
                  )}
                </div>
              </div>

              {hasRealEmail ? (
                <>
                  <div className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ 
                    color: '#3e2615', 
                    opacity: 0.8,
                    fontSize: iosPadSmallFontSize
                  }}>
                    Email Status: {account.email_verified ? (
                      <span style={{ color: '#22c55e' }}>✓ Verified</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>✗ Not Verified</span>
                    )}
                  </div>

                  {!account.email_verified && (
                    <div className={isMobileLandscape ? 'mt-2' : 'mt-3'}>
                      <VerifyEmailButton account={account} />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ 
                    color: '#3e2615', 
                    opacity: 0.8,
                    fontSize: iosPadSmallFontSize
                  }}>
                    Account Type: <span style={{ color: '#6b7280' }}>Guest Mode</span>
                  </div>
                  <div className={isMobileLandscape ? 'mt-2' : 'mt-3'}>
                    <button
                      onClick={() => setShowCompleteRegistrationModal(true)}
                    className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all`}
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                      border: '2px solid #8b6542',
                      fontSize: iosPadSmallFontSize
                    }}
                    >
                      Complete Registration
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Security (always shown) */}
          {hasRealEmail && (
          <div>
            <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-3' : 'p-6'}`} style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542'
            }}>
              <h2 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold ${isMobileLandscape ? 'mb-2' : 'mb-4'}`} style={{ 
                color: '#3e2615',
                fontSize: iosPadHeaderFontSize
              }}>Security</h2>
              <div className={isMobileLandscape ? 'space-y-2' : 'space-y-4'}>
                <div>
                  <p className={`${isMobileLandscape ? 'text-xs mb-2' : 'text-sm mb-3'}`} style={{ 
                    color: '#3e2615', 
                    opacity: 0.8,
                    fontSize: iosPadSmallFontSize
                  }}>
                    Forgot your password? We'll send a reset link to your email address.
                  </p>
                  <button
                    onClick={handleResetPassword}
                    disabled={resetPasswordStatus === "loading" || !account.email}
                    className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-6 py-3'} rounded-xl font-semibold transition-all disabled:opacity-50`}
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                      border: '2px solid #8b6542',
                      fontSize: iosPadBodyFontSize
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(2px)';
                      }
                    }}
                    onMouseUp={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                  >
                    {resetPasswordStatus === "loading" ? "Sending..." : "Reset Password"}
                  </button>
                </div>
                {resetPasswordMessage && (
                  <div
                    className={`${isMobileLandscape ? 'text-xs p-2' : 'text-sm p-3'} rounded-lg ${
                      resetPasswordStatus === "success" ? "bg-green-100" : "bg-red-100"
                    }`}
                    style={{
                      color: resetPasswordStatus === "success" ? "#166534" : "#991b1b",
                      fontSize: iosPadSmallFontSize
                    }}
                  >
                    {resetPasswordMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Additional sections for non-guest users - full width below */}
        {hasRealEmail && (
          <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-6'} ${isMobileLandscape ? 'mt-2' : 'mt-6'}`}>
            {/* Balance & Gear Section */}
            <div>
              <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-3' : 'p-6'}`} style={{
                background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                borderColor: '#8b6542'
              }}>
                <h2 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold ${isMobileLandscape ? 'mb-2' : 'mb-4'}`} style={{ 
                  color: '#3e2615',
                  fontSize: iosPadHeaderFontSize
                }}>Balance & Gear</h2>
              <div className={isMobileLandscape ? 'space-y-2' : 'space-y-3'}>
                <div className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                  <span style={{ color: '#3e2615' }}>Glint Balance:</span>
                  <span className="font-semibold" style={{ color: '#3e2615' }}>
                    ✧ {parseFloat(account.glint_balance).toFixed(2)} Glint
                  </span>
                </div>
                <div className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                  <span style={{ color: '#3e2615' }}>Bank:</span>
                  <span className="font-semibold" style={{ color: '#3e2615' }}>
                    ✧ {parseFloat(account.banked_glint || '0').toFixed(2)} Glint
                  </span>
                </div>
                <div className={`border-t ${isMobileLandscape ? 'pt-2 mt-2' : 'pt-3 mt-3'}`} style={{ borderColor: '#8b6542' }}>
                  <h3 className={`font-semibold ${isMobileLandscape ? 'mb-1 text-sm' : 'mb-2'}`} style={{ 
                    color: '#3e2615',
                    fontSize: isIOSPad && isMobileLandscape ? '18px' : undefined
                  }}>Deposit to Bank</h3>
                  <p className={`${isMobileLandscape ? 'text-[10px] mb-1' : 'text-xs mb-2'}`} style={{ 
                    color: '#3e2615', 
                    opacity: 0.7,
                    fontSize: iosPadSmallFontSize
                  }}>
                    Banked glint is permanently locked and cannot be withdrawn. Use it to qualify for tournaments.
                  </p>
                  <form onSubmit={handleDeposit} className={isMobileLandscape ? 'space-y-1' : 'space-y-2'}>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={parseFloat(account.glint_balance)}
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder="Amount"
                        className={`flex-1 rounded-lg ${isMobileLandscape ? 'p-1.5 text-xs' : 'p-2 text-sm'} outline-none`}
                        style={{
                          border: '2px solid #c4a07a',
                          background: '#ffffff',
                          color: '#3e2615',
                          fontSize: iosPadBodyFontSize
                        }}
                        disabled={depositStatus === "loading"}
                      />
                      <button
                        type="submit"
                        disabled={depositStatus === "loading" || !depositAmount || parseFloat(depositAmount) <= 0}
                        className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all disabled:opacity-50`}
                        style={{
                          background: '#fffaeb',
                          color: '#3e2615',
                          boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                          border: '2px solid #8b6542',
                          fontSize: iosPadSmallFontSize
                        }}
                      >
                        {depositStatus === "loading" ? "..." : "Deposit"}
                      </button>
                    </div>
                    {depositMessage && (
                      <div
                        className={`${isMobileLandscape ? 'text-[10px] p-1.5' : 'text-xs p-2'} rounded ${
                          depositStatus === "success" ? "bg-green-100" : "bg-red-100"
                        }`}
                        style={{
                          color: depositStatus === "success" ? "#166534" : "#991b1b",
                          fontSize: iosPadSmallFontSize
                        }}
                      >
                        {depositMessage}
                      </div>
                    )}
                  </form>
                </div>
                <div className={`border-t ${isMobileLandscape ? 'pt-2 mt-2' : 'pt-3 mt-3'}`} style={{ borderColor: '#8b6542' }}>
                  <h3 className={`font-semibold ${isMobileLandscape ? 'mb-1 text-sm' : 'mb-2'}`} style={{ 
                    color: '#3e2615',
                    fontSize: isIOSPad && isMobileLandscape ? '18px' : undefined
                  }}>Gear</h3>
                  <div className={isMobileLandscape ? 'space-y-1' : 'space-y-2'}>
                    {/* Free Basic Shovel */}
                    {account.equipment_charges > 0 && (
                      <div className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                        <span style={{ color: '#3e2615' }}>Free Basic Shovel:</span>
                        <span className="font-semibold" style={{ color: '#3e2615' }}>
                          {account.equipment_charges}
                        </span>
                      </div>
                    )}
                    {/* Purchased gear by product */}
                    {account.purchased_gear_by_product && typeof account.purchased_gear_by_product === 'object' && 
                     Object.entries(account.purchased_gear_by_product)
                      .filter(([_, count]) => {
                        const num = typeof count === 'number' ? count : parseInt(String(count), 10) || 0;
                        return num > 0;
                      })
                      .sort(([a], [b]) => {
                        // Sort by product name for consistent display
                        const nameA = PRODUCT_NAMES[a] || a;
                        const nameB = PRODUCT_NAMES[b] || b;
                        return nameA.localeCompare(nameB);
                      })
                      .map(([productId, count]) => {
                        const num = typeof count === 'number' ? count : parseInt(String(count), 10) || 0;
                        return (
                          <div key={productId} className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                            <span style={{ color: '#3e2615' }}>{PRODUCT_NAMES[productId] || productId}:</span>
                            <span className="font-semibold" style={{ color: '#3e2615' }}>
                              {num}
                            </span>
                          </div>
                        );
                      })}
                    {/* Fallback for old data format */}
                    {!account.purchased_gear_by_product && (account.purchased_gear_charges || 0) > 0 && (
                      <div className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                        <span style={{ color: '#3e2615' }}>Purchased Gear (total):</span>
                        <span className="font-semibold" style={{ color: '#3e2615' }}>
                          {account.purchased_gear_charges} entries
                        </span>
                      </div>
                    )}
                    {/* Tournament Won Gear */}
                    {(account.tournament_won_gear || 0) > 0 && (
                      <div className={`flex items-center justify-between ${isMobileLandscape ? 'text-xs' : ''}`} style={{ fontSize: iosPadBodyFontSize }}>
                        <span style={{ color: '#3e2615' }}>Tournament Won:</span>
                        <span className="font-semibold" style={{ color: '#3e2615' }}>
                          {account.tournament_won_gear} entries
                        </span>
                      </div>
                    )}
                    {/* No gear message */}
                    {account.equipment_charges === 0 && 
                     (!account.purchased_gear_by_product || Object.values(account.purchased_gear_by_product).every(count => {
                       const num = typeof count === 'number' ? count : parseInt(String(count), 10) || 0;
                       return num === 0;
                     })) &&
                     (account.purchased_gear_charges || 0) === 0 &&
                     (account.tournament_won_gear || 0) === 0 && (
                      <div className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ 
                        color: '#3e2615', 
                        opacity: 0.7,
                        fontSize: iosPadBodyFontSize
                      }}>
                        No gear available
                      </div>
                    )}
                  </div>
                </div>
                {isAndroid && (
                  <div className={`border-t ${isMobileLandscape ? 'pt-2 mt-2' : 'pt-3 mt-3'}`} style={{ borderColor: '#8b6542' }}>
                    <h3 className={`font-semibold ${isMobileLandscape ? 'mb-1 text-sm' : 'mb-2'}`} style={{ color: '#3e2615' }}>Remove Ads</h3>
                    <p className={`${isMobileLandscape ? 'text-[10px] mb-1' : 'text-xs mb-2'}`} style={{ color: '#3e2615', opacity: 0.7 }}>
                      Disable rewarded ads on this device.
                    </p>
                    <button
                      onClick={handleRemoveAdsPurchase}
                      disabled={adsRemovedLocal || removeAdsStatus === "loading"}
                      className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all disabled:opacity-50`}
                      style={{
                        background: '#fffaeb',
                        color: '#3e2615',
                        boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                        border: '2px solid #8b6542'
                      }}
                    >
                      {adsRemovedLocal ? "Ads Removed" : removeAdsStatus === "loading" ? "Processing..." : "Remove Ads"}
                    </button>
                    {removeAdsMessage && (
                      <div
                        className={`${isMobileLandscape ? 'text-[10px] p-1.5' : 'text-xs p-2'} rounded mt-2 ${
                          removeAdsStatus === "success" ? "bg-green-100" : "bg-red-100"
                        }`}
                        style={{
                          color: removeAdsStatus === "success" ? "#166534" : "#991b1b"
                        }}
                      >
                        {removeAdsMessage}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* Account Info Section */}
            <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-3' : 'p-6'}`} style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542'
            }}>
              <h2 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold ${isMobileLandscape ? 'mb-2' : 'mb-4'}`} style={{ 
                color: '#3e2615',
                fontSize: iosPadHeaderFontSize
              }}>Account Info</h2>
              <div className={`${isMobileLandscape ? 'space-y-1 text-xs' : 'space-y-2 text-sm'}`} style={{ 
                color: '#3e2615', 
                opacity: 0.8,
                fontSize: iosPadSmallFontSize
              }}>
                <div>Member since: {new Date(account.created_at).toLocaleDateString()}</div>
                <div>Last updated: {new Date(account.updated_at).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        )}

        {hasRealEmail && (
          <div className={isMobileLandscape ? 'mt-2' : 'mt-6'}>
            <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-3' : 'p-6'}`} style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542'
            }}>
              <h2 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold ${isMobileLandscape ? 'mb-2' : 'mb-3'}`} style={{ 
                color: '#3e2615',
                fontSize: iosPadHeaderFontSize
              }}>Delete My Account</h2>
              <div className={isMobileLandscape ? 'space-y-1 text-xs' : 'space-y-2 text-sm'} style={{ 
                color: '#3e2615', 
                opacity: 0.9,
                fontSize: iosPadSmallFontSize
              }}>
                <p>Request deletion of your Goldrush account and personal data any time. Requests are processed within 30 days.</p>
                <p>Use the link below to open the deletion form. Submit it with the email on your account so we can verify the request.</p>
              </div>
              <div className={`${isMobileLandscape ? 'mt-2' : 'mt-4'}`}>
                <Link
                  to="/delete-account"
                  className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-base'} inline-flex items-center justify-center rounded-xl font-semibold transition-all`}
                  style={{
                    background: '#fffaeb',
                    color: '#3e2615',
                    boxShadow: '0 3px 0 #8b6542, 0 5px 10px rgba(0,0,0,0.25)',
                    border: '2px solid #8b6542',
                    minWidth: isMobileLandscape ? '100%' : '240px',
                    fontSize: iosPadBodyFontSize
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(2px)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                >
                  Delete My Account
                </Link>
              </div>
            </div>
          </div>
        )}
          </div>
          {isMobileLandscape && (
            <>
              {showTopIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ top: '-23px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 7L6 2L11 7" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              {showBottomIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ bottom: '-20px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 1L6 6L11 1" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <LimitReachedModal
        isOpen={showCompleteRegistrationModal}
        onClose={() => setShowCompleteRegistrationModal(false)}
        onCompleteRegistration={async () => {
          setShowCompleteRegistrationModal(false);
          if (onAccountRefresh) {
            await onAccountRefresh();
          }
        }}
        apiBase={API_BASE}
        hasEmail={hasRealEmail}
        allowClose={true}
        title="Complete Registration"
      />
    </PageLayout>
  );
}
